import { ProductSelectDto, UserProfileRole } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { cn } from '@lib/utils';
import { useGetCurrentUser } from '@queries/users.query';
import { ArrowRight, ChevronDownIcon } from 'lucide-react';
import { FunctionComponent, useState } from 'react';
import { Link } from 'react-router-dom';
import { TooltipComponent } from '../../../core/tooltip.component';

type ProductContainerProps = {
  className?: string;
  hideHover?: boolean;
  onNavigateToDetails: (id: string) => void;
  product: ProductSelectDto;
};

export const ProductContainer: FunctionComponent<ProductContainerProps> = (props) => {
  const { hideHover = false, product, onNavigateToDetails, className } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const technologyCount = product.technologies?.length || 0;

  const { data: user } = useGetCurrentUser();
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;

  return (
    <div
      className={cn('flex flex-col gap-2 h-fit rounded-md bg-gray-100 p-2', className)}
      onMouseEnter={hideHover ? undefined : () => setIsHovering(true)}
      onMouseLeave={hideHover ? undefined : () => setIsHovering(false)}
    >
      <div className="bg-white rounded-md flex flex-col gap-2 p-2">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-0">
            <div
              className={cn('text-gray-600 font-medium text-base cursor-pointer', !hideHover && 'hover:underline')}
              onClick={() => onNavigateToDetails(product.id)}
            >
              {product.name}
            </div>
            <BlueLabel text="Product" />
          </div>
          {isInnovator && !hideHover && (
            <IconButton
              icon={<ArrowRight />}
              isVisible={isHovering}
              onClick={() => onNavigateToDetails(product.id)}
              variant="secondary"
            />
          )}
        </div>

        <div
          className={cn('flex justify-between items-center', technologyCount > 0 && 'cursor-pointer')}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="text-xs font-normal text-[#1F1C1AB2]">
            {technologyCount} Technolog{technologyCount === 1 ? 'y' : 'ies'}
          </div>

          {technologyCount > 0 && (
            <ChevronDownIcon
              size={20}
              className={cn('text-[#1F1C1AB2] transition-transform duration-200', isOpen && 'rotate-180')}
            />
          )}
        </div>
      </div>

      {isOpen && technologyCount > 0 && (
        <div className="flex flex-col gap-2 font-light text-xs text-gray-500">
          {product.technologies?.map((technology, index) => (
            <div key={technology.id}>
              {index > 0 && <div className="h-px border-t border-gray-300"></div>}
              <div className="flex flex-col gap-2 p-2">
                <div className="flex flex-col gap-0">
                  <Link to={`/technology/${technology.id}`}>
                    <div className="text-gray-600 font-medium hover:underline text-sm">{technology.name}</div>
                  </Link>
                  <BlueLabel
                    className="text-xs"
                    text="Technology"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    <div>Category: {technology.category?.name || 'N/A'}</div>
                    {technology.category?.description && (
                      <TooltipComponent
                        side="right"
                        className="text-xs whitespace-pre-wrap font-normal"
                        triggerClassName="text-gray-500 bg-white"
                      >
                        {technology.category.description}
                      </TooltipComponent>
                    )}
                  </div>
                  {technology.subcategory && <div>Subcategory: {technology.subcategory.name}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
